import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ALL_ID = -1;

interface MultiSelectPickerOption {
  readonly id: number;
  readonly label: string;
  readonly disabled?: boolean;
}

interface MultiSelectPickerProps {
  label: string;
  allLabel: string;
  itemLabelKey: string;
  options: MultiSelectPickerOption[];
  selectedIds: number[] | null;
  onChange: (ids: number[] | null) => void;
  size?: 'small' | 'medium';
}

export const MultiSelectPicker = ({
  label,
  allLabel,
  itemLabelKey,
  options,
  selectedIds,
  onChange,
  size = 'medium',
}: MultiSelectPickerProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  if (options.length === 0) return null;

  const allOptions = [{ id: ALL_ID, label: allLabel }, ...options];
  const selectedCount = selectedIds === null ? options.length : selectedIds.length;
  const excludedCount = options.length - selectedCount;
  const itemLabel = t(itemLabelKey, { count: selectedCount });
  const summary =
    selectedIds === null
      ? allLabel
      : excludedCount > 0
      ? `${selectedCount} ${itemLabel}, ${excludedCount} ${t(
          'pages.playout_reports.multi_select.excluded',
        )}`
      : `${selectedCount} ${itemLabel}`;

  return (
    <Autocomplete
      autoHighlight
      disableCloseOnSelect
      multiple
      size={size}
      options={allOptions}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionDisabled={(option) => option.disabled === true}
      value={
        selectedIds === null
          ? allOptions
          : allOptions.filter((o) => o.id !== ALL_ID && selectedIds.includes(o.id))
      }
      onChange={(_, selected) => {
        const hasAll = selected.some((o) => o.id === ALL_ID);
        const hadAll = selectedIds === null;
        onChange(
          hasAll && !hadAll
            ? null
            : !hasAll && hadAll
            ? []
            : selected.filter((o) => o.id !== ALL_ID).map((o) => o.id),
        );
      }}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        if (reason !== 'reset') setInputValue(value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('common.actions.search')}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (e) => {
              if (e.key === 'Backspace' && e.currentTarget.value === '') {
                e.stopPropagation();
              }
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const isDisabled = option.disabled === true;
        return (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{
              ...(isDisabled && { color: 'text.disabled', fontStyle: 'italic' }),
            }}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize="small" />}
              checkedIcon={<CheckBox fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
              disabled={isDisabled}
            />
            {option.label}
          </Box>
        );
      }}
      renderTags={() => <Chip label={summary} size="small" sx={{ ml: 0.5 }} />}
      sx={{ width: 'min(320px, 100%)' }}
      slotProps={{
        popper: {
          style: { width: 'fit-content', minWidth: 'min(320px, 100vw)' },
          placement: 'bottom-start',
        },
      }}
    />
  );
};
